import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { Navigate, useNavigate } from '@lib/react-router-dom'

import { useEntitySettings } from '@context/EntityContext'
import { useSignup } from '@context/SignupContext'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { PhoneVerificationWorkflowEnum } from '@model/CreatePhoneNumberVerification'
import { LeadDto, isLeadSourceMobileRegistration } from '@model/LeadDto'
import { NameDto } from '@model/NameDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useLinksConfig } from '@hooks/useLinksConfig'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import MobileAppRedirectSuccessPage from './MobileAppRedirectSuccessPage'
import { SignupError } from './SignupErrorPage'

export const VerifyEmailRedirect: React.FC = () => {
  const { leadId } = useParams()
  const [searchParams] = useSearchParams()

  if (leadId) {
    return (
      <Navigate
        to={`/sign-up/personal-details/contact/verify-email?${searchParams.toString()}`}
        state={{ leadId }}
      />
    )
  } else {
    return <Navigate to='/login' />
  }
}

const isVerificationCodeError = (verificationCodeError?: string): boolean => {
  return (
    verificationCodeError === 'phone_verification_max_attempts_reached' ||
    verificationCodeError === 'max_new_verification_code_requests_reached'
  )
}

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [, setEntity] = useEntitySettings()

  const location = useLocation()
  const { leadId } = location.state || {}

  const { setLead, signupData } = useSignup()

  const apiClient = useApiClient(ClientApiClient)
  const navigate = useNavigate()
  const leadEmail = useRef('')
  const { links } = useLinksConfig()

  const [loading, setLoading] = useState(true)
  const [, persistLead] = useLocallyPersistedState<LeadDto | undefined>('lead', undefined)

  const handleStocksRedirect = (lead: LeadDto): boolean => {
    const products = lead.products
    const hasOnlyStocks =
      products.length > 0 && products.every((p: NameDto) => p.id === TickmillProductType.STOCKS)
    if (hasOnlyStocks) {
      window.location.href = links.IBKR
      return true
    }
    return false
  }

  const verifyEmail = useCallback(async () => {
    const hash = searchParams.get('hash')
    const token = searchParams.get('token')

    try {
      if (!leadId || !token) {
        return navigate('/login')
      }

      const leadDto = await apiClient.getLeadByIdAndToken(leadId, token)
      setEntity({ entity: leadDto.tickmillCompany.id })
      persistLead(leadDto)

      try {
        if (hash) {
          await apiClient.verifyEmail({ email: leadDto.email, token: hash })
          if (handleStocksRedirect(leadDto)) return
        }
      } catch (e: unknown) {
        if (handleStocksRedirect(leadDto)) return
        if (instanceOfAxiosError((e as ResponseError)?.response)) {
          const errorResponse = (e as ResponseError).response.response?.data
          if (errorResponse?.code === SignupError.LeadVerificationErrorException) {
            await setLead(leadDto)
            if (
              !leadDto.phoneVerificationRequired &&
              !isLeadSourceMobileRegistration(leadDto.leadSource)
            ) {
              navigate(`/sign-up/personal-details/info/?${searchParams.toString()}`, {
                state: { leadId },
              })
              return
            }
          } else if (errorResponse?.code) {
            throw e
          }
        }
      }

      if (isLeadSourceMobileRegistration(leadDto?.leadSource)) {
        leadEmail.current = leadDto.email
        return setLead(leadDto)
      }

      if (leadDto.phoneVerificationRequired) {
        const createdPhoneVerification = await apiClient.createPhoneNumberVerification(leadDto.id, {
          phoneNumber: leadDto.phoneNumber,
          leadId: leadDto.id,
          countryCode: leadDto.countryCode,
          countryId: leadDto.country.id,
          workflowId: PhoneVerificationWorkflowEnum.WorkflowSMS,
        })
        leadEmail.current = leadDto.email
        await setLead(leadDto)
        return navigate(
          `/sign-up/personal-details/contact/verify-phone/${leadId}?token=${token}&phoneVerificationId=${createdPhoneVerification.phoneVerificationId}`
        )
      } else {
        leadEmail.current = leadDto.email
        await setLead(leadDto)
        navigate(`/sign-up/personal-details/info/?${searchParams.toString()}`, {
          state: { leadId },
        })
      }
    } catch (err: unknown) {
      if ((err as ResponseError).response?.response?.data.code) {
        const errorResponse = (err as ResponseError).response?.response?.data
        if (errorResponse?.code) {
          ;(err as ResponseError).preventDefault?.()
          if (isVerificationCodeError(errorResponse?.code)) {
            navigate(`/sign-up/personal-details/info/?${searchParams.toString()}`, {
              state: { leadId },
            })
          } else if (errorResponse?.code === SignupError.BackToLogin) {
            return navigate('/login')
          } else {
            const errorSearchParams = new URLSearchParams()
            errorSearchParams.set('error', errorResponse?.code)
            errorSearchParams.set('leadId', leadId)
            if (leadEmail.current) {
              errorSearchParams.set('leadEmail', leadEmail.current || '')
            }
            if (errorResponse?.properties?.isLeadVerified) {
              errorSearchParams.set('verified', 'true')
            }
            navigate(`../error?${errorSearchParams.toString()}`)
          }
        }
      }
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, searchParams])

  useEffect(() => {
    verifyEmail()
  }, [verifyEmail])

  return (
    <React.Fragment>
      {!isLeadSourceMobileRegistration(signupData.lead?.leadSource) && (
        <Loading showLoadingIcon isLoading={loading} />
      )}
      {isLeadSourceMobileRegistration(signupData.lead?.leadSource) && (
        <MobileAppRedirectSuccessPage lead={signupData.lead} />
      )}
    </React.Fragment>
  )
}
